import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Fan affinity laws — performance curve with two operating points showing N₁/N₂ scaling. */
export function DiagramFanAffinityLaws() {
  // Chart area
  const ox = 50, oy = 200, w = 250, h = 160;

  // Two fan curves at N1 and N2 (parabolic-ish drops)
  const curve = (N: number, color: string, dash?: string) => {
    let d = `M${ox},${oy - N * 80}`;
    for (let i = 1; i <= 30; i++) {
      const t = i / 30;
      const xp = ox + t * w;
      const drop = N * 80 - Math.pow(t * w, 2) * (0.0018 / N);
      const yp = oy - Math.max(drop, 6);
      d += ` L${xp},${yp}`;
    }
    return <Path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeDasharray={dash} />;
  };

  // Operating points (system-curve intersections)
  const op1x = ox + 90, op1y = oy - 110;
  const op2x = ox + 145, op2y = oy - 65;

  return (
    <DiagramCanvas>
      {/* Axes */}
      <Line x1={ox} y1={oy} x2={ox + w} y2={oy} stroke={DT.dim} strokeWidth="1.4" />
      <Line x1={ox} y1={oy} x2={ox} y2={oy - h} stroke={DT.dim} strokeWidth="1.4" />
      <SvgText x={ox + w / 2} y={oy + 18} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">CFM (Q) →</SvgText>
      <SvgText x={ox - 14} y={oy - h / 2} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle"
        rotation="-90" originX={ox - 14} originY={oy - h / 2}>SP (TP) ↑</SvgText>

      {/* System curve (parabolic up) */}
      <Path d={`M${ox + 5},${oy - 5} Q${ox + 90},${oy - 60} ${ox + w - 5},${oy - h + 10}`}
        fill="none" stroke={DT.dim} strokeWidth="1" strokeDasharray="4,3" />
      <SvgText x={ox + w - 6} y={oy - h + 8} fill={DT.dim} fontSize="8" fontFamily={DT.mono} textAnchor="end">system curve</SvgText>

      {/* Fan curve at N1 (lower speed) */}
      {curve(1.0, DT.leader)}
      <SvgText x={ox + 25} y={oy - 88} fill={DT.leader} fontSize="8" fontFamily={DT.mono}>N₁</SvgText>

      {/* Fan curve at N2 (higher speed) — orange */}
      {curve(1.5, DT.varColor, '6,3')}
      <SvgText x={ox + 25} y={oy - 130} fill={DT.varColor} fontSize="8" fontFamily={DT.mono}>N₂</SvgText>

      {/* Operating points */}
      <Circle cx={op1x} cy={op1y} r={5} fill={DT.leader} stroke={DT.stroke} strokeWidth="1" />
      <Circle cx={op2x} cy={op2y} r={5} fill={DT.varColor} stroke={DT.stroke} strokeWidth="1" />
      <SvgText x={op1x - 8} y={op1y - 6} fill={DT.leader} fontSize="9" fontFamily={DT.mono} fontWeight="700" textAnchor="end">1</SvgText>
      <SvgText x={op2x + 8} y={op2y - 6} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} fontWeight="700">2</SvgText>

      {/* Projection lines from op1 */}
      <Line x1={op1x} y1={op1y} x2={op1x} y2={oy} stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,2" />
      <Line x1={op1x} y1={op1y} x2={ox} y2={op1y} stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,2" />
      <Line x1={op2x} y1={op2y} x2={op2x} y2={oy} stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,2" />
      <Line x1={op2x} y1={op2y} x2={ox} y2={op2y} stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,2" />
      <SvgText x={op1x} y={oy + 9} fill={DT.leader} fontSize="8" fontFamily={DT.mono} textAnchor="middle">Q₁</SvgText>
      <SvgText x={op2x} y={oy + 9} fill={DT.varColor} fontSize="8" fontFamily={DT.mono} textAnchor="middle">Q₂</SvgText>

      {/* Affinity-laws cheat box */}
      <Rect x={ox + 165} y={oy - h - 8} width={88} height={42} fill="rgba(0,0,0,0.5)" stroke={DT.varColor} strokeWidth="0.8" rx="2" />
      <SvgText x={ox + 209} y={oy - h + 4} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">Q₂/Q₁ = N₂/N₁</SvgText>
      <SvgText x={ox + 209} y={oy - h + 16} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">SP ∝ N²</SvgText>
      <SvgText x={ox + 209} y={oy - h + 28} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">HP ∝ N³</SvgText>

      {/* Labels */}
      <LeaderLine x1={op1x} y1={op1y} x2={20} y2={50} varName="N₁" desc="op pt 1" anchor="start" />
      <LeaderLine x1={op2x} y1={op2y} x2={310} y2={50} varName="N₂" desc="op pt 2" anchor="end" />

      <FormulaTag formula="Q₂/Q₁ = N₂/N₁    SP ∝ N²    HP ∝ N³" />
    </DiagramCanvas>
  );
}
